import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Actions} from '../../../../common/actions';
import {
  Button, Card, Container, Divider, Form,
  Grid, Icon, Input, Tab, Radio,
} from 'semantic-ui-react';
import {getDropdownOptions} from '../../../../common/util/dropdown';
import '../styles.scss';
import CourseDocumentsTab from './documentsTab';

const {
  getWtsCoursesAreas,
  getWtsCourse,
  getWtsCourses,
  getWtsSkills,
  changeWtsCourseForm,
  cleanWtsCourseForm,
  createWtsCourse,
  updateWtsCourse,
} = Actions;

const WtsCourseForm = () => {
  const {courseId} = useParams();
  const navigate = useNavigate();
  const {areas, areaSelected} = useSelector((state) => state.wtsAreas);
  const {coursesAreas} = useSelector((state) => state.wtsCoursesAreas);
  const {courses, loading} = useSelector((state) => state.wtsCourses);
  const {skills} = useSelector((state) => state.wtsSkills);
  const courseForm = useSelector((state) => state.wtsCourses.form);
  const user = useSelector((state) => state.auth?.user);
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(cleanWtsCourseForm());
    dispatch(getWtsSkills(-1));
    dispatch(changeWtsCourseForm('directed_to', []));
    dispatch(changeWtsCourseForm('skills', []));
    dispatch(changeWtsCourseForm('required_skills_count', 0));
    if (!courseId) {
      ['prerequisite', 'quiz_value', 'writen_test_value', 'practices_value', 'approval_value', 'pre_evaluation_needed']
        .forEach((field) => dispatch(changeWtsCourseForm(field, 0)));
    }
    
    if (areaSelected?.id) {
      dispatch(getWtsCoursesAreas(areaSelected.id));
    }
    
    if (courseId) {
      dispatch(getWtsCourse(courseId));
    }

    return () => {
      dispatch(cleanWtsCourseForm());
      dispatch(getWtsSkills(-1));
    };
  }, [courseId, dispatch, areaSelected?.id]);

  useEffect(() => {
    if (courseId && courseForm?.skills && !courseForm.required_skills_count) {
      const count = courseForm.skills.length;
      dispatch(changeWtsCourseForm('required_skills_count', count));
    }
  }, [courseId, courseForm?.skills, dispatch]);

  useEffect(() => {
    if (!courseId && areaSelected?.id) {
      dispatch(changeWtsCourseForm('area', areaSelected.id));
      dispatch(changeWtsCourseForm('skills', []));
      dispatch(changeWtsCourseForm('required_skills_count', 0));
    }
  }, [areaSelected?.id, courseId, dispatch]);

  useEffect(() => {
    if (courseForm.course_area && courseForm.course_area.length !== 0) {
      if (!courseId) {
        dispatch(changeWtsCourseForm('skills', []));
        dispatch(changeWtsCourseForm('required_skills_count', 0));
      }
      dispatch(getWtsCourses({
        'area': courseForm.area,
        'course_area': courseForm.course_area,
      }));
      dispatch(getWtsSkills({
        'area': courseForm.area,
        'course_area': courseForm.course_area,
      }));
    }
  }, [courseForm.course_area, courseForm.area, courseId, dispatch]);

  const handleCourseFormValue = (field, value) => {
    dispatch(changeWtsCourseForm(field, value));
  };

  const handleAreaChange = (e, {value}) => {
    handleCourseFormValue('course_area', value);
    if (!courseId) {
      handleCourseFormValue('skills', []);
      handleCourseFormValue('required_skills_count', 0);
    }
    const selectedAreaObj = coursesAreas.find((a) => a.id === value);
    if (selectedAreaObj && !courseId) {
      const prefix = selectedAreaObj.id || '0';
      const sequential = (selectedAreaObj.sequence || 0) + 1;
      handleCourseFormValue('reference', `${prefix}.${sequential}`);
    }
  };

  const onSave = async(e) => {
    e.preventDefault();
    if (saving) {
      return;
    }
    const data = {
      ...courseForm,
      'user': user?.username || user?.id || 'SISTEMA',
    };
    setSaving(true);
    try {
      if (courseId) {
        await dispatch(updateWtsCourse(courseId, data));
      } else {
        await dispatch(createWtsCourse(data));
      }
      navigate('/wts/configuracion/cursos');
    } catch (error) {
      // Falla de creación/actualización: el mensaje de error ya se muestra vía messageBox,
      // se evita navegar para que el usuario pueda corregir y reintentar sin perder lo escrito.
    } finally {
      setSaving(false);
    }
  };

  const renderCourseDetailTabPane = () => {
    const activeCoursesAreas = coursesAreas.filter((a) => {
      const isActive = a.status === 'ACTIVO' || a.status === 'A';
      const isSelected = courseId && String(a.id) === String(courseForm?.course_area);
      return isActive || isSelected;
    });

    const filteredCourses = courses.filter((c) => {
      const isNotCurrent = c.id.toString() !== courseId?.toString();
      const isActive = c.status === 'ACTIVO' || c.status === 'A';
      return isNotCurrent && isActive;
    });

    const activeSkills = skills.filter((s) => s.status === 'ACTIVO' || s.status === 'A');
    
    const selectedSkillsCount = courseForm?.skills?.length || 0;
    const totalRequiredSkills = courseForm?.required_skills_count || 0;

    return (
      <Card raised fluid className="tab-card">
        <Card.Content>
          <Form.Group widths="equal">
            <Form.Field width={3}>
              <Form.Dropdown required fluid selection disabled label="Departamento"
                options={getDropdownOptions(areas, 'id', 'description')}
                value={courseForm?.area} />
            </Form.Field>
            <Form.Field width={4}>
              <Form.Dropdown required fluid selection label="Área"
                disabled={!!courseId} value={courseForm?.course_area || ''}
                options={getDropdownOptions(activeCoursesAreas, 'id', 'course_area')}
                onChange={handleAreaChange} />
            </Form.Field>
            <Form.Field width={5}>
              <Form.Input fluid required label="Título del Curso" value={courseForm?.course || ''}
                onChange={(e, {value}) => handleCourseFormValue('course', value?.toUpperCase())} />
            </Form.Field>
            <Form.Field width={2}>
              <Form.Input fluid required label="Código" value={courseForm?.reference || ''} readOnly />
            </Form.Field>
            <Form.Field width={2}>
              <Form.Input fluid label="Nro. Habilidades" type="number"
                value={courseForm?.required_skills_count || 0}
                onChange={(e, {value}) => handleCourseFormValue('required_skills_count', Number(value))} />
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <Form.Field>
              <Form.Dropdown required fluid selection label="Tipo Certificación" value={courseForm?.type || ''}
                options={getDropdownOptions([{'type': 'TDE'}, {'type': 'TMC'}], 'type', 'type')}
                onChange={(e, {value}) => handleCourseFormValue('type', value)} />
            </Form.Field>
            <Form.Field>
              <Form.Input input={<Input label="Meses" type="number" min={0} />}
                fluid required label="Tiempo Experiencia" value={courseForm?.prerequisite || 0}
                onChange={(e, {value}) => handleCourseFormValue('prerequisite', Number(value))} />
            </Form.Field>
            <Form.Field>
              <Form.Input input={<Input label="Nota" type="number" min={0} />}
                fluid required label="Cuestionarios" value={courseForm?.quiz_value || 0}
                onChange={(e, {value}) => handleCourseFormValue('quiz_value', Number(value))} />
            </Form.Field>
            <Form.Field>
              <Form.Input input={<Input label="%" type="number" max={100} min={0} />}
                fluid required label="Examen Escrito" value={courseForm?.writen_test_value || 0}
                onChange={(e, {value}) => handleCourseFormValue('writen_test_value', Number(value))} />
            </Form.Field>
            <Form.Field>
              <Form.Input input={<Input label="%" type="number" max={100} min={0} />}
                fluid required label="Valor Prácticas" value={courseForm?.practices_value || 0}
                onChange={(e, {value}) => handleCourseFormValue('practices_value', Number(value))} />
            </Form.Field>
            <Form.Field>
              <Form.Input input={<Input label="%" type="number" max={100} min={0} />}
                fluid required label="Valor Aprobación" value={courseForm?.approval_value || 0}
                onChange={(e, {value}) => handleCourseFormValue('approval_value', Number(value))} />
            </Form.Field>
            <Form.Field className="flex col center middle">
              <label>Pre Evaluación</label>
              <div style={{'marginTop': '5px'}}>
                <Radio slider className={courseForm?.pre_evaluation_needed === 1 ? 'green' : ''}
                  label={courseForm?.pre_evaluation_needed === 1 ? 'SI' : 'NO'}
                  checked={courseForm?.pre_evaluation_needed === 1}
                  onChange={(e, {checked}) => handleCourseFormValue('pre_evaluation_needed', checked ? 1 : 0)} />
              </div>
            </Form.Field>
          </Form.Group>
          <Grid stackable>
            <Grid.Column width={6}>
              <Form.Field>
                <label>Dirigido A ({filteredCourses.length})</label>
                <div className="chk-max-h">
                  {courseForm?.course_area && filteredCourses.length === 0 && (
                    <div className="flex center middle">
                      <Icon name="info circle" color="blue" size="big" />
                      <span>No hay otros cursos activos</span>
                    </div>
                  )}
                  {courseForm?.course_area && filteredCourses.map((course, i) => (
                    <Form.Checkbox key={`${i}${course.id}`} label={course.course} value={course.id}
                      checked={courseForm?.directed_to?.includes(course.id) || false} className="chk-mb blue"
                      onChange={(e, {value, checked}) => {
                        const directedTo = courseForm?.directed_to || [];
                        const newValue = checked ? [...directedTo, value] : directedTo.filter((id) => id !== value);
                        handleCourseFormValue('directed_to', newValue);
                      }} />
                  ))}
                </div>
              </Form.Field>
              <Divider fitted style={{'margin': '15px 0'}} />
              <Form.Field>
                <label>
                  Habilidades ({activeSkills.length})
                  <span style={{'marginLeft': '5px', 'color': '#2185d0'}}>
                    [{selectedSkillsCount} / {totalRequiredSkills}]
                  </span>
                </label>
                <div className="chk-max-h">
                  {courseForm?.course_area && activeSkills.length === 0 && (
                    <div className="flex center middle">
                      <Icon name="info circle" color="blue" size="big" />
                      <span>No hay habilidades activas</span>
                    </div>
                  )}
                  {courseForm?.course_area && activeSkills.map((skill, i) => (
                    <Form.Checkbox key={`${i}${skill.id}`} label={skill.description} value={skill.id}
                      checked={courseForm?.skills?.includes(skill.id) || false} className="chk-mb blue"
                      onChange={(e, {value, checked}) => {
                        const current = courseForm?.skills || [];
                        const next = checked ? [...current, value] : current.filter((id) => id !== value);
                        handleCourseFormValue('skills', next);
                      }} />
                  ))}
                </div>
              </Form.Field>
            </Grid.Column>
            <Grid.Column width={10}>
              <Form.TextArea label="Descripción / Observaciones" rows={18}
                value={courseForm?.course_observation || ''}
                onChange={(e, {value}) => handleCourseFormValue('course_observation', value)} />
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>
    );
  };

  return (
    <Container fluid>
      <Form className="left-text" onSubmit={onSave}>
        <Card raised fluid>
          <div className="title-bar">
            <div className="title">
              <h2>{courseId ? 'Edición' : 'Creación'} de Curso</h2>
              <div className="title-bar-actions">
                <div className="flex end">
                  <Button secondary type="submit" loading={saving || loading} disabled={saving || loading}
                    className='btn-guardar'>
                    <Icon link name="save" size="big" color="green" className='item-btn' />Guardar
                  </Button>
                  <Button type="button" secondary size="big" compact
                    style={{'padding': '2px 0 8px 0', 'marginLeft': '10px'}}
                    onClick={() => navigate('/wts/configuracion/cursos')}
                    icon={<Icon name="log out" size="big" color="red" link />} />
                </div>
              </div>
            </div>
          </div>
        </Card>
        <Tab panes={[
          {
            'menuItem': {'key': 'courseDet', 'content': 'DETALLE DEL CURSO'},
            'render': () => renderCourseDetailTabPane(),
          },
          {
            'menuItem': {'key': 'courseDocs', 'content': 'DOCUMENTOS DEL CURSO', 'disabled': !courseId},
            'render': () => <CourseDocumentsTab courseForm={courseForm} />,
          },
        ]} />
      </Form>
    </Container>
  );
};

export default WtsCourseForm;
